import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { api } from '@/lib/api';
import { User } from '@/types';
import { formatFullName } from '@/lib/format';

interface MyConsentRecord {
  id: string;
  printedName: string;
  consentType: 'video' | 'audio' | 'either';
  signedAt: string;
  revokedAt?: string | null;
  signatureData?: string | null;
  isCurrent: boolean;
}

export interface MyRecordingConsentModalProps {
  isOpen: boolean;
  user: User;
  onClose: () => void;
  onUpdated: () => void | Promise<void>;
}

function consentTypeLabel(type: MyConsentRecord['consentType']) {
  switch (type) {
    case 'video':
      return 'Video recording';
    case 'audio':
      return 'Audio-only recording';
    case 'either':
      return 'Video or audio recording';
    default:
      return type;
  }
}

function formatSignedAt(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function MyRecordingConsentModal({
  isOpen,
  user,
  onClose,
  onUpdated,
}: MyRecordingConsentModalProps) {
  const [consents, setConsents] = useState<MyConsentRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  const loadConsents = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get<MyConsentRecord[]>('/me/recording-consents');
    if (res.error) {
      setError(res.error);
      setConsents([]);
    } else {
      const items = res.data ?? [];
      setConsents(items);
      setSelectedId(items[0]?.id ?? null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      void loadConsents();
    }
  }, [isOpen, loadConsents]);

  const selected = consents.find((c) => c.id === selectedId) ?? consents[0] ?? null;
  const leaderName = formatFullName(user.firstName, user.lastName) || user.email;

  const handleRevoke = async () => {
    setRevoking(true);
    setError(null);
    const res = await api.post<User>('/me/recording-consent/revoke', {});
    if (res.error) {
      setError(res.error);
    } else {
      setShowRevokeConfirm(false);
      await loadConsents();
      await onUpdated();
    }
    setRevoking(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="My Recording Consent"
        size="lg"
        panelClassName="max-w-2xl"
      >
        {loading && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading your consent forms…</p>
        )}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {!loading && consents.length === 0 && (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            You have not signed a recording consent form yet.
          </p>
        )}
        {!loading && consents.length > 0 && selected && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {consents.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Signed forms
                </label>
                <select
                  value={selected.id}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                >
                  {consents.map((consent) => (
                    <option key={consent.id} value={consent.id}>
                      {formatSignedAt(consent.signedAt)}
                      {consent.revokedAt ? ' (revoked)' : consent.isCurrent ? ' (current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {selected.isCurrent && !selected.revokedAt && (
                <Badge variant="success">Current consent</Badge>
              )}
              {selected.revokedAt && <Badge variant="default">Revoked</Badge>}
              {!selected.isCurrent && !selected.revokedAt && (
                <Badge variant="default">Superseded</Badge>
              )}
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Signed {formatSignedAt(selected.signedAt)}
              </span>
            </div>

            <p className="text-sm text-slate-700 dark:text-slate-300">
              I, <strong>{leaderName}</strong>, gave permission to Times Square Church to record my
              voice, image, likeness, and words as I share prayer responses.
            </p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Participation option: {consentTypeLabel(selected.consentType)}
            </p>

            {selected.signatureData && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Your signature
                </label>
                <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white overflow-hidden">
                  <img
                    src={selected.signatureData}
                    alt={`Signature of ${selected.printedName}`}
                    className="w-full h-40 object-contain bg-white"
                  />
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Printed name
                </label>
                <p className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm">
                  {selected.printedName}
                </p>
              </div>
              {selected.revokedAt && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Revoked
                  </label>
                  <p className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm">
                    {formatSignedAt(selected.revokedAt)}
                  </p>
                </div>
              )}
            </div>

            {user.recordingConsentRevoked && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                You are not currently participating in recordings. Sign a new consent form when you
                respond to a prayer request if you wish to record again.
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {selected.isCurrent && !selected.revokedAt && (
                <Button variant="outline" onClick={() => setShowRevokeConfirm(true)}>
                  Revoke consent
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={handleRevoke}
        title="Revoke recording consent?"
        message={
          <>
            You will stop participating in future audio and video prayer responses until you sign a
            new consent form. This does not affect recordings already shared with prayer requesters.
          </>
        }
        confirmLabel="Revoke consent"
        variant="danger"
        isLoading={revoking}
      />
    </>
  );
}
